/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import FormValidations, { ValidationsObject } from 'cio/utils/form-validations';
import { task } from 'ember-concurrency';

import { isBlank } from '@ember/utils';
import AccountService from 'cio/services/account';
import ReleaseNotificationService from 'cio/services/release-notification';
import ApiService from 'cio/services/api';
import { DataCenter } from 'cio/models/account';
import AlertsService from 'cio/services/alerts';
import { EventsType } from 'cio/utils/analytics/events';
import AnalyticsService from 'cio/services/analytics';
import { registerDestructor } from '@ember/destroyable';
import { EventProperties } from 'cio/services/analytics';
import BillingService from 'cio/services/billing';
import { PlanType } from 'cio/models/plan';
import isValidEmail from 'cio/utils/validators/email';
import { currentOfferingsByPlanType } from 'cio/utils/billing/plans';
import InlineContractsSalesBodyPage from 'cio/components/inline-contracts-sales/body-page';
import InlineContractsSalesThankYouPage from 'cio/components/inline-contracts-sales/thank-you-page';
import { ComponentLike } from '@glint/template';

enum Page {
  BodyPage = 'inline-contracts-sales/body-page',
  ThankYouPage = 'inline-contracts-sales/thank-you-page',
}

enum PageAnimation {
  Next = 'slide-from-right',
  Prev = 'slide-from-left',
}

const PAGES_ORDER = [Page.BodyPage];

type Validations = Partial<Record<Page, ValidationsObject>>;

const VALIDATIONS: Validations = {
  [Page.BodyPage]: {
    message(value: string) {
      if (isBlank(value)) {
        return 'Fields cannot be blank.';
      }
      return true;
    },
    features(value: string) {
      if (isBlank(value)) {
        return 'Fields cannot be blank.';
      }
      return true;
    },
    replyTo(value: string) {
      if (isBlank(value)) {
        return 'Email should not be empty.';
      }
      if (!isValidEmail(value)) {
        return 'Invalid email format.';
      }
      return true;
    },
  },
};

function defaultFormValues(): FormValues {
  return {
    message: '',
    features: '',
    replyTo: '',
  };
}

type FormValues = {
  message: string;
  features: string;
  replyTo: string;
};

export type InlineContractsSalesFormHandlers = {
  formValues: FormValues;
  setFormValue: (typeof InlineContractsSales.prototype)['setFormValue'];
  onSubmit?: () => unknown;
};

export interface InlineContractsSalesNavigationHandlers {
  onCancel: () => unknown;
  onClose: () => unknown;
}

export type InlineContractsSalesPageArgs = InlineContractsSalesFormHandlers &
  InlineContractsSalesNavigationHandlers & {
    trackEvent: (typeof InlineContractsSales.prototype)['trackEvent'];
    planClicked?: PlanType;
  };

interface InlineContractsSalesArgs {
  onCancel: InlineContractsSalesNavigationHandlers['onCancel'];
  onClose: InlineContractsSalesNavigationHandlers['onClose'];
  planClicked?: PlanType;
}

export default class InlineContractsSales extends Component<InlineContractsSalesArgs> {
  @service analytics!: AnalyticsService;
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service auth!: AuthService;
  @service releaseNotification!: ReleaseNotificationService;
  @service billing!: BillingService;

  @tracked currentPageIndex = 0;
  @tracked container: HTMLDivElement | null = null;
  @tracked submitted = false;
  @tracked formValues = defaultFormValues();

  @tracked validations: Partial<Record<Page, FormValidations>> = {};

  @tracked isCancelModalVisible = false;

  constructor(owner: unknown, args: InlineContractsSalesArgs) {
    super(owner, args);

    /** The setTimeout avoids "You attempted to update (...) but it had already
     * been used previously in the same computation" error */
    setTimeout(() => {
      this.formValues = {
        ...this.formValues,
        replyTo: this.auth.user?.email ?? '',
      };
    });

    for (let page of Object.values(Page)) {
      this.validations[page] = FormValidations.create({
        validations: VALIDATIONS[page],
        context: this,
        modelKey: 'formValues',
      });
    }

    this.trackEvent('inline_contracts_sales_form_opened');

    registerDestructor(this, () => {
      this.trackEvent('inline_contracts_sales_form_closed');
    });
  }

  get currentPlan() {
    if (this.billing.billingInfo?.trial === true) {
      return 'trial';
    }
    let { subscription } = this.billing;
    if (
      subscription?.active === true &&
      (subscription?.plan.planType === PlanType.NotReportable || subscription?.plan.planType === PlanType.Basic)
    ) {
      return 'legacy';
    }
    return subscription?.plan.planType as string;
  }

  @action
  trackEvent(name: keyof EventsType['inline_contracts_sales'], eventData?: EventProperties) {
    let currentAccount = this.account.current;
    let { currentUser } = currentAccount;

    let data = {
      account_id: currentAccount.id,
      requester_name: currentUser?.fullName,
      requester_email: currentUser?.email,
      requester_user_id: currentUser?.id,
      current_plan: this.currentPlan,
      requested_plan: this.args.planClicked,
      ...eventData,
    };
    this.analytics.track('inline_contracts_sales', name, { data });
  }

  get currentPage() {
    if (this.submitted) {
      return Page.ThankYouPage;
    }
    return PAGES_ORDER[this.currentPageIndex];
  }

  get currentPageComponent() {
    let componentMap: Record<Page, ComponentLike<any>> = {
      [Page.BodyPage]: InlineContractsSalesBodyPage,
      [Page.ThankYouPage]: InlineContractsSalesThankYouPage,
    };

    return componentMap[this.currentPage];
  }

  get currentValidation() {
    return this.validations[this.currentPage];
  }

  get planClicked() {
    return this.args.planClicked;
  }

  get inbox() {
    if (this.billing.billingInfo?.trial === true) {
      return 'sales';
    }
    let currentPlan = this.billing.subscription?.plan.planType;
    if (currentOfferingsByPlanType.includes(currentPlan ?? PlanType.NotReportable)) {
      return 'premium';
    }
    return 'upgrades';
  }

  @action
  handleCancel() {
    this.trackEvent('inline_contracts_sales_form_cancel_clicked');
    if (this.hasUserInput) {
      this.isCancelModalVisible = true;
    } else {
      this.args.onCancel();
    }
  }

  @action
  handleClose() {
    this.args.onClose();
  }

  /** Checks if the user has inserted any data in the form,
   * so we can show a confirmation modal before closing it */
  get hasUserInput() {
    // We require data in both the body and the features fields
    let { message, features } = this.formValues;
    return message.trim().length > 0 || features.trim().length > 0;
  }

  @action
  handleCloseCancelModal() {
    this.trackEvent('inline_contracts_sales_form_cancel_dismissed');
    this.isCancelModalVisible = false;
  }

  validatePage = task(async (page: Page) => {
    try {
      let validation = this.validations[page];
      let result = (await validation?.validateModel()) ?? Promise.resolve(true);
      return result;
    } catch (result) {
      return false;
    }
  });

  /** This function serves as a sanity check before submitting, should never be false because
   * all pages have been validated already, but might catch edge cases. */
  fullValidation = task(async () => {
    let validatedPages = await Promise.all(PAGES_ORDER.map((page) => this.validatePage.perform(page)));

    let firstInvalidPage = validatedPages.indexOf(false);

    return {
      validated: firstInvalidPage === -1,
      firstInvalidPage,
    };
  });

  submit = task({ drop: true }, async (event: SubmitEvent) => {
    event.preventDefault();
    this.trackEvent('inline_contracts_sales_form_submit_clicked');
    let { validated, firstInvalidPage } = await this.fullValidation.perform();

    if (validated === false) {
      this.currentPageIndex = firstInvalidPage;
      return;
    }

    let result = await this.submitTask.perform();

    // TODO: there's currently no design for error states. We're relying on the
    // "uh oh" toast in case of network errors but that's about it.
    if (result === true) {
      this.trackEvent('inline_contracts_sales_form_submit_success');
      this.submitted = true;
    } else {
      this.trackEvent('inline_contracts_sales_form_submit_error');
    }
  });

  triggerAnimation(animation: PageAnimation) {
    this.container?.classList.remove(...(Object.values(PageAnimation) as string[]));
    requestAnimationFrame(() => {
      this.container?.classList.add(animation);
    });
  }

  @action
  didInsertContainer(element: HTMLDivElement) {
    this.container = element;
  }

  /* Tracks the first time the user changes a field, mainly to track which
   * fields are *not* being changed for now. */
  trackFormValueChange = (() => {
    let hasTrackedFormValueChange: Record<keyof FormValues, boolean> = Object.assign(
      {},
      ...Object.keys(this.formValues).map((key) => ({ [key]: false })),
    );

    return (field: keyof FormValues) => {
      if (!hasTrackedFormValueChange[field]) {
        this.trackEvent('inline_contracts_sales_form_field_input', { field });
        hasTrackedFormValueChange[field] = true;
      }
    };
  })();

  @action
  setFormValue<T extends keyof FormValues>(key: T, value: FormValues[T]) {
    this.trackFormValueChange(key);

    this.formValues = {
      ...this.formValues,
      [key]: value,
    };
  }

  submitTask = task({ drop: true }, async () => {
    if (this.auth.user == null) {
      this.alerts.error(
        'privacy.update-contracts-sales-access.user-missing',
        'Unable to find current user. Refresh the page and try again.',
      );
      return;
    }

    let { email: accountEmail, id: accountId } = this.account.current;
    let { firstName: userFirstName, lastName: userLastName } = this.auth.user;
    let { replyTo, message, features } = this.formValues;

    let { version } = this.releaseNotification.original;

    let data = {
      accountEmail,
      accountId,
      userFirstName,
      userLastName,
      message,
      features,
      replyTo,
      url: window.location.href,
      browser: window.navigator.userAgent,
      windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
      version,
    };

    let { inbox } = this;

    try {
      await this.api.post(`${inbox}`, { data, pathScope: 'account', dataCenter: DataCenter.US });
      // TODO: Currently the API always returns empty values, so we're not
      // listening to the result of the API call.
      return true;
    } catch {
      return false;
    }
  });
}
